import { Component } from '@theme/component';
import { ThemeEvents, ZoomMediaSelectedEvent } from '@theme/events';

const FLAVOR_CLASS_PREFIX = 'media-flavor--';
const FLAVOR_OPTION_PATTERN = /\b(flavor|flavour|taste)\b|^type$/i;

/**
 * Converts a flavor option value into a class-safe handle.
 * @param {string} value
 * @returns {string}
 */
function toFlavorHandle(value) {
  return value
    .toLowerCase()
    .trim()
    .replace(/^variant:/, '')
    .replace(/['"]/g, '')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
}

/**
 * A custom element that renders a media gallery.
 *
 * @typedef {object} Refs
 * @property {import('./zoom-dialog').ZoomDialog} [zoomDialogComponent] - The zoom dialog component.
 * @property {import('./slideshow').Slideshow} [slideshow] - The slideshow component.
 * @property {HTMLElement[]} [media] - The media elements.
 *
 * @extends Component<Refs>
 */
export class MediaGallery extends Component {
  connectedCallback() {
    super.connectedCallback();

    const { signal } = this.#controller;
    document.addEventListener(ThemeEvents.variantUpdate, this.#handleVariantUpdate, { signal });
    document.addEventListener(ThemeEvents.variantSelected, this.#handleVariantSelected, { signal });
    this.refs.zoomDialogComponent?.addEventListener(ThemeEvents.zoomMediaSelected, this.#handleZoomMediaSelected, {
      signal,
    });

    this.#filterBySelectedFlavor();
  }

  #controller = new AbortController();

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#controller.abort();
  }

  /**
   * Handles a variant update event.
   */
  #handleVariantUpdate = () => {
    this.#applyFlavorFilter();
  };

  /**
   * Filters gallery icons as soon as a Flavor option is selected.
   */
  #handleVariantSelected = () => {
    this.#applyFlavorFilter();
  };

  /**
   * Handles the 'zoom-media:selected' event.
   * @param {ZoomMediaSelectedEvent} event - The zoom-media:selected event.
   */
  #handleZoomMediaSelected = async (event) => {
    this.slideshow?.select(event.detail.index, undefined, { animate: false });
  };

  #applyFlavorFilter() {
    const flavorClass = this.#getSelectedFlavorClass();
    if (flavorClass) this.dataset.selectedFlavor = flavorClass;
    this.#filterBySelectedFlavor(flavorClass);
    this.#selectFirstVisibleSlide();
  }

  /**
   * Shows only gallery icons that match the selected Flavor picker.
   * @param {string} [flavorClass]
   */
  #filterBySelectedFlavor(flavorClass = this.dataset.selectedFlavor || this.#getSelectedFlavorClass()) {
    if (!flavorClass) return;

    const items = Array.from(
      this.querySelectorAll('slideshow-slide, .slideshow-controls__thumbnail, .media-gallery__grid li')
    );
    const featuredMap = this.#getFeaturedFlavorMap();
    const knownFlavors = this.#getKnownFlavorHandles();
    /** @type {string} */
    let inheritedFlavor = '';
    let lastKind = '';
    let matchCount = 0;
    let assignedCount = 0;

    items.forEach((item) => {
      const kind = item.tagName === 'SLIDESHOW-SLIDE' ? 'slide' : item.matches('.slideshow-controls__thumbnail') ? 'thumb' : 'grid';
      if (kind !== lastKind) {
        inheritedFlavor = '';
        lastKind = kind;
      }
      const mediaId = item.dataset.mediaId || item.querySelector('[data-media-id]')?.getAttribute('data-media-id') || '';

      if (mediaId && featuredMap[mediaId]) {
        inheritedFlavor = featuredMap[mediaId];
      }

      const itemFlavor =
        item.dataset.flavorClass ||
        this.#getFlavorClassFromElement(item) ||
        this.#flavorFromAlt(item, knownFlavors) ||
        inheritedFlavor;

      if (itemFlavor) assignedCount += 1;

      const matches = itemFlavor === flavorClass;
      if (matches) matchCount += 1;

      item.classList.toggle('media-flavor-hidden', Boolean(itemFlavor) && !matches);
      item.toggleAttribute('hidden', Boolean(itemFlavor) && !matches);

      if (itemFlavor && !matches) {
        item.style.setProperty('display', 'none', 'important');
      } else {
        item.style.removeProperty('display');
      }
    });

    this.#syncThumbnailIndexes();

    if (assignedCount > 0 && matchCount === 0) {
      items.forEach((item) => {
        item.classList.remove('media-flavor-hidden');
        item.removeAttribute('hidden');
        item.style.removeProperty('display');
      });
    }
  }

  /**
   * Keeps visible thumbnail clicks aligned with the visible slides.
   */
  #syncThumbnailIndexes() {
    const visibleSlides = Array.from(this.querySelectorAll('slideshow-slide')).filter(
      (slide) => !slide.classList.contains('media-flavor-hidden') && !slide.hidden
    );

    this.querySelectorAll('.slideshow-controls__thumbnail').forEach((thumb) => {
      if (!(thumb instanceof HTMLElement) || thumb.classList.contains('media-flavor-hidden')) return;

      const mediaId = thumb.dataset.mediaId;
      const slideIndex = visibleSlides.findIndex((slide) => {
        const slideId = slide.dataset.mediaId || slide.querySelector('[data-media-id]')?.getAttribute('data-media-id');
        return slideId === mediaId;
      });

      if (slideIndex >= 0) {
        thumb.setAttribute('on:click', `/select/${slideIndex}`);
      }
    });
  }

  /**
   * Featured media IDs mapped to flavor handles.
   * @returns {Record<string, string>}
   */
  #getFeaturedFlavorMap() {
    const script = this.querySelector('script[data-flavor-featured-map]');
    if (!script?.textContent) return {};

    try {
      return JSON.parse(script.textContent);
    } catch {
      return {};
    }
  }

  /**
   * Flavor handles from the variant picker.
   * @returns {string[]}
   */
  #getKnownFlavorHandles() {
    const picker = this.#getFlavorPicker();
    if (!picker) return [];

    return Array.from(picker.querySelectorAll('[data-flavor-class]'))
      .map((el) => el.dataset.flavorClass)
      .filter((value) => Boolean(value));
  }

  /**
   * @param {Element} item
   * @param {string[]} knownFlavors
   * @returns {string}
   */
  #flavorFromAlt(item, knownFlavors) {
    const alts = Array.from(item.querySelectorAll('img'))
      .map((img) => img.getAttribute('alt') || img.alt || '')
      .filter(Boolean);

    for (const alt of alts) {
      const altHandle = toFlavorHandle(alt);
      if (!altHandle) continue;
      if (knownFlavors.includes(altHandle)) return altHandle;

      const match = knownFlavors.find(
        (flavor) => altHandle.includes(flavor) || alt.toLowerCase().includes(flavor.replace(/-/g, ' '))
      );
      if (match) return match;
    }

    return '';
  }

  /**
   * Reads a flavor handle from an element's media-flavor-- class.
   * @param {Element} item
   * @returns {string}
   */
  #getFlavorClassFromElement(item) {
    const flavorClass = Array.from(item.classList).find((className) => className.startsWith(FLAVOR_CLASS_PREFIX));
    if (flavorClass) return flavorClass.slice(FLAVOR_CLASS_PREFIX.length);

    const child = item.querySelector(`[class*="${FLAVOR_CLASS_PREFIX}"]`);
    if (child) {
      const childClass = Array.from(child.classList).find((className) => className.startsWith(FLAVOR_CLASS_PREFIX));
      if (childClass) return childClass.slice(FLAVOR_CLASS_PREFIX.length);
    }

    return '';
  }

  /**
   * @returns {Element | null}
   */
  #getFlavorPicker() {
    const section = this.closest('.shopify-section');
    return section?.querySelector('variant-picker') || document.querySelector('variant-picker');
  }

  /**
   * Reads the selected Flavor class from the variant picker.
   * @returns {string}
   */
  #getSelectedFlavorClass() {
    const picker = this.#getFlavorPicker();

    if (picker) {
      const fieldsets = picker.querySelectorAll('fieldset.variant-option');

      for (const fieldset of fieldsets) {
        const legend = fieldset.querySelector('legend')?.textContent?.trim() ?? '';
        if (!FLAVOR_OPTION_PATTERN.test(legend)) continue;

        const selected = fieldset.querySelector('input:checked');
        if (selected instanceof HTMLElement) {
          return (
            selected.dataset.flavorClass ||
            toFlavorHandle(selected.value || selected.getAttribute('aria-label') || '')
          );
        }
      }

      const selectedOption = picker.querySelector('select option[selected], fieldset input:checked');
      if (selectedOption instanceof HTMLElement) {
        return (
          selectedOption.dataset.flavorClass ||
          toFlavorHandle(selectedOption.value || selectedOption.textContent || '')
        );
      }
    }

    return this.dataset.selectedFlavor || '';
  }

  /**
   * Selects the first visible slide after flavor filtering.
   */
  #selectFirstVisibleSlide() {
    const slides = Array.from(this.querySelectorAll('slideshow-slide'));
    const firstVisibleIndex = slides.findIndex(
      (slide) => !slide.hidden && !slide.classList.contains('media-flavor-hidden')
    );

    if (firstVisibleIndex >= 0) {
      this.slideshow?.select(0, undefined, { animate: false });
    }
  }

  /**
   * Zooms the media gallery.
   *
   * @param {number} index - The index of the media to zoom.
   * @param {PointerEvent} event - The pointer event.
   */
  zoom(index, event) {
    this.refs.zoomDialogComponent?.open(index, event);
  }

  get slideshow() {
    return this.refs.slideshow;
  }

  get media() {
    return this.refs.media;
  }

  get presentation() {
    return this.dataset.presentation;
  }
}

if (!customElements.get('media-gallery')) {
  customElements.define('media-gallery', MediaGallery);
}
